import { useCallback, useEffect, useState } from 'react';
import { DUEL_CARD_DEFINITIONS, DUEL_PACKS, EVENTS } from '@flip7/shared';
import type { DuelCard as DuelCardType, DuelStats, DuelChallengeNotifyPayload } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { DuelCard } from '../components/DuelCard';
import { socket } from '../lib/socket';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface DuelPubScreenProps {
  onBack: () => void;
}

interface Friend {
  id: string;
  name: string;
  activeAvatar: string | null;
}

export function DuelPubScreen({ onBack }: DuelPubScreenProps) {
  const { accessToken, user, refreshUser } = useAuth();
  const [collection, setCollection] = useState<DuelCardType[]>([]);
  const [stats, setStats] = useState<(DuelStats & { starterPackClaimed: boolean }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [newCards, setNewCards] = useState<DuelCardType[] | null>(null);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [showFriends, setShowFriends] = useState(false);
  const [challengeSent, setChallengeSent] = useState<string | null>(null);
  const [incomingChallenge, setIncomingChallenge] = useState<DuelChallengeNotifyPayload | null>(null);

  const [duelStep, setDuelStep] = useState<'pub' | 'wager' | 'loadout'>('pub');
  const [activeMatchId, setActiveMatchId] = useState<string | null>(null);
  const [selectedWagerCard, setSelectedWagerCard] = useState<string | null>(null);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedPerk, setSelectedPerk] = useState<string | null>(null);
  const [eligible, setEligible] = useState<{ eligible: boolean; reason?: string } | null>(null);
  const [history, setHistory] = useState<Array<{ id: string; opponentName: string; opponentAvatar: string | null; won: boolean | null; createdAt: string }>>([]);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    const h = authHeaders();
    const [collRes, statsRes, eligRes, histRes] = await Promise.all([
      fetch(`${API_BASE}/api/duel/collection`, { headers: h }),
      fetch(`${API_BASE}/api/duel/stats`, { headers: h }),
      fetch(`${API_BASE}/api/duel/eligibility`, { headers: h }),
      fetch(`${API_BASE}/api/duel/history`, { headers: h }),
    ]);
    if (collRes.ok) setCollection(await collRes.json());
    if (statsRes.ok) setStats(await statsRes.json());
    if (eligRes.ok) setEligible(await eligRes.json());
    if (histRes.ok) setHistory(await histRes.json());
    setLoading(false);
  }, [accessToken, authHeaders]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadFriends = async () => {
    const res = await fetch(`${API_BASE}/api/friends`, { headers: authHeaders() });
    if (res.ok) setFriends(await res.json());
    setShowFriends(true);
  };

  const claimStarter = async () => {
    setBuying(true);
    const res = await fetch(`${API_BASE}/api/duel/starter-pack`, { method: 'POST', headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      setNewCards(data.cards);
      await loadData();
    }
    setBuying(false);
  };

  const openPack = async (type: string) => {
    setBuying(true);
    const res = await fetch(`${API_BASE}/api/duel/packs/${type}`, { method: 'POST', headers: authHeaders() });
    if (res.ok) {
      const data = await res.json();
      setNewCards(data.cards);
      await refreshUser();
      await loadData();
    }
    setBuying(false);
  };

  const challengeFriend = (friendId: string) => {
    socket.emit(EVENTS.DUEL_CHALLENGE, { targetUserId: friendId });
    setChallengeSent(friendId);
    setShowFriends(false);
    setTimeout(() => setChallengeSent(null), 5000);
  };

  useSocketEvent<DuelChallengeNotifyPayload>(EVENTS.DUEL_CHALLENGE, (data) => {
    setIncomingChallenge(data);
  });

  const acceptChallenge = () => {
    if (!incomingChallenge) return;
    socket.emit(EVENTS.DUEL_CHALLENGE_ACCEPT, { matchId: incomingChallenge.matchId });
    setActiveMatchId(incomingChallenge.matchId);
    setDuelStep('wager');
    setIncomingChallenge(null);
  };

  const declineChallenge = () => {
    if (!incomingChallenge) return;
    socket.emit(EVENTS.DUEL_CHALLENGE_DECLINE, { matchId: incomingChallenge.matchId });
    setIncomingChallenge(null);
  };

  useSocketEvent<{ matchId: string }>('duelChallengeCreated', (data) => {
    setActiveMatchId(data.matchId);
    setDuelStep('wager');
  });

  useSocketEvent<{ phase: string }>('duelPhaseChange', (data) => {
    if (data.phase === 'loadout') setDuelStep('loadout');
  });

  const submitWager = () => {
    if (!activeMatchId || !selectedWagerCard) return;
    socket.emit(EVENTS.DUEL_WAGER_SUBMIT, { matchId: activeMatchId, cardId: selectedWagerCard });
  };

  const submitLoadout = () => {
    if (!activeMatchId) return;
    socket.emit(EVENTS.DUEL_LOADOUT_SUBMIT, {
      matchId: activeMatchId,
      actionCardIds: selectedActions,
      perkCardId: selectedPerk,
    });
  };

  const toggleAction = (cardId: string) => {
    setSelectedActions(prev =>
      prev.includes(cardId) ? prev.filter(id => id !== cardId) : prev.length < 2 ? [...prev, cardId] : prev
    );
  };

  if (!accessToken) {
    return (
      <div className="duel-arena">
        <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
          <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
            <button onClick={onBack} className="back-btn">&larr;</button>
            <h1 className="section-header" style={{ flex: 1, fontSize: '1.6rem' }}>Duel Arena</h1>
          </div>
          <p style={{ textAlign: 'center', color: '#6A6050', fontSize: '0.8rem' }}>Sign in to enter the arena.</p>
        </div>
      </div>
    );
  }

  const actions = collection.filter(c => DUEL_CARD_DEFINITIONS[c.cardType]?.category === 'action');
  const perks = collection.filter(c => DUEL_CARD_DEFINITIONS[c.cardType]?.category === 'perk');
  const nonWagered = collection.filter(c => c.id !== selectedWagerCard);

  return (
    <div className="duel-arena">
      <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
        <div className="page-header" style={{ marginBottom: 'var(--space-xl)' }}>
          <button onClick={onBack} className="back-btn">&larr;</button>
          <h1 className="section-header" style={{ flex: 1, fontSize: '1.6rem' }}>Duel Arena</h1>
          {user && <div className="coin-badge">{user.coins}</div>}
        </div>

        {/* ── Incoming Challenge ── */}
        {incomingChallenge && (
          <div className="duel-challenge-banner duel-pub__section">
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: '#E57373', marginBottom: 4 }}>
              DUEL CHALLENGE!
            </p>
            <p style={{ fontSize: '0.75rem', color: '#E8E0D0', marginBottom: 14 }}>
              {incomingChallenge.fromUser.avatar} <strong>{incomingChallenge.fromUser.name}</strong> wants to fight
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn-arena btn-arena--primary" onClick={acceptChallenge}>Accept</button>
              <button className="btn-arena" onClick={declineChallenge}>Decline</button>
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#6A6050' }}>Loading...</p>
        ) : duelStep === 'wager' ? (
          /* ── Wager ── */
          <div className="arena-surface arena-surface--glow duel-pub__section" style={{ padding: '20px' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: '#DAA520', textAlign: 'center', marginBottom: 4, textShadow: '0 0 20px rgba(218,165,32,0.3)' }}>
              CHOOSE YOUR WAGER
            </p>
            <p style={{ fontSize: '0.65rem', color: '#6A6050', textAlign: 'center', marginBottom: 16 }}>
              The winner takes the loser's card
            </p>
            <div className="duel-pub__card-grid">
              {collection.map(c => (
                <DuelCard key={c.id} cardType={c.cardType} compact selected={selectedWagerCard === c.id} onClick={() => setSelectedWagerCard(c.id)} />
              ))}
            </div>
            <button className="btn-arena btn-arena--primary" onClick={submitWager} disabled={!selectedWagerCard} style={{ width: '100%', marginTop: 16 }}>
              Lock In
            </button>
          </div>

        ) : duelStep === 'loadout' ? (
          /* ── Loadout ── */
          <div className="arena-surface arena-surface--glow duel-pub__section" style={{ padding: '20px' }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: '#DAA520', textAlign: 'center', marginBottom: 4, textShadow: '0 0 20px rgba(218,165,32,0.3)' }}>
              ARM YOURSELF
            </p>
            <p style={{ fontSize: '0.65rem', color: '#6A6050', textAlign: 'center', marginBottom: 16 }}>
              Up to 2 actions + 1 perk. Consumed after the duel.
            </p>

            <p className="drawer-section-label" style={{ color: '#8A7A60' }}>Actions (max 2)</p>
            <div className="duel-pub__card-grid" style={{ marginBottom: 14 }}>
              {nonWagered.filter(c => DUEL_CARD_DEFINITIONS[c.cardType]?.category === 'action').map(c => (
                <DuelCard key={c.id} cardType={c.cardType} compact selected={selectedActions.includes(c.id)} onClick={() => toggleAction(c.id)} />
              ))}
            </div>

            <p className="drawer-section-label" style={{ color: '#8A7A60' }}>Perk (max 1)</p>
            <div className="duel-pub__card-grid" style={{ marginBottom: 16 }}>
              {nonWagered.filter(c => DUEL_CARD_DEFINITIONS[c.cardType]?.category === 'perk').map(c => (
                <DuelCard key={c.id} cardType={c.cardType} compact selected={selectedPerk === c.id} onClick={() => setSelectedPerk(prev => prev === c.id ? null : c.id)} />
              ))}
            </div>

            <button className="btn-arena btn-arena--primary" onClick={submitLoadout} style={{ width: '100%' }}>
              Ready to Duel
            </button>
          </div>

        ) : (
          /* ── Main Pub ── */
          <>
            {/* Stats */}
            {stats && (
              <div className="arena-surface duel-pub__section">
                <div className="duel-pub__stats">
                  <div className="duel-pub__stat">
                    <div className="duel-pub__stat-value">{stats.rating}</div>
                    <div className="duel-pub__stat-label">Rating</div>
                  </div>
                  <div className="duel-pub__stat">
                    <div className="duel-pub__stat-value">{stats.wins}</div>
                    <div className="duel-pub__stat-label">Wins</div>
                  </div>
                  <div className="duel-pub__stat">
                    <div className="duel-pub__stat-value">{stats.losses}</div>
                    <div className="duel-pub__stat-label">Losses</div>
                  </div>
                  <div className="duel-pub__stat">
                    <div className="duel-pub__stat-value">{stats.winStreak}</div>
                    <div className="duel-pub__stat-label">Streak</div>
                  </div>
                </div>
              </div>
            )}

            {/* Starter Pack */}
            {stats && !stats.starterPackClaimed && (
              <div className="arena-surface arena-surface--glow duel-pub__section arena-welcome">
                <p className="arena-welcome__title">Enter the Arena</p>
                <p className="arena-welcome__sub">Claim your free starter pack to begin collecting duel cards</p>
                <button className="btn-arena btn-arena--primary" onClick={claimStarter} disabled={buying}>
                  {buying ? 'Opening...' : 'Claim Starter Pack'}
                </button>
              </div>
            )}

            {/* Pack Reveal */}
            {newCards && (
              <div className="arena-surface arena-surface--glow duel-pub__section" style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: '0.9rem', color: '#DAA520', marginBottom: 14, textShadow: '0 0 20px rgba(218,165,32,0.4)' }}>
                  NEW CARDS!
                </p>
                <div className="pack-reveal" style={{ position: 'relative' }}>
                  <div className="pack-reveal__burst" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                  {newCards.map((c, i) => (
                    <div key={c.id} className="pack-reveal__card" style={{ '--reveal-delay': `${i * 0.2}s` } as React.CSSProperties}>
                      <DuelCard cardType={c.cardType} compact />
                    </div>
                  ))}
                </div>
                <button className="btn-arena" onClick={() => setNewCards(null)} style={{ marginTop: 16 }}>
                  Continue
                </button>
              </div>
            )}

            {/* Pack Shop */}
            {stats?.starterPackClaimed && (
              <div className="arena-surface duel-pub__section">
                <p className="drawer-title" style={{ marginBottom: 10 }}>Card Packs</p>
                <button className="btn-arena btn-arena--primary" onClick={() => openPack('common')}
                  disabled={buying || (user?.coins ?? 0) < DUEL_PACKS.common.price}
                  style={{ width: '100%' }}>
                  Common Pack — {DUEL_PACKS.common.price} coins
                </button>
              </div>
            )}

            {/* Challenge */}
            {stats?.starterPackClaimed && collection.length > 0 && (
              <div className="arena-surface arena-surface--glow duel-pub__section">
                <p className="drawer-title" style={{ marginBottom: 10 }}>Challenge</p>
                {challengeSent && (
                  <p style={{ fontSize: '0.7rem', color: '#DAA520', marginBottom: 8, textAlign: 'center' }}>Challenge sent! Waiting for response...</p>
                )}
                {!showFriends ? (
                  <button className="btn-arena btn-arena--danger" onClick={loadFriends} style={{ width: '100%' }}>
                    Challenge a Friend
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {friends.length === 0 ? (
                      <p style={{ fontSize: '0.7rem', color: '#6A6050', textAlign: 'center' }}>No friends to challenge</p>
                    ) : friends.map(f => (
                      <button key={f.id} onClick={() => challengeFriend(f.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                          padding: '10px 14px', border: '1px solid rgba(218,165,32,0.15)',
                          borderRadius: '8px', background: 'rgba(255,255,255,0.03)', color: '#E8E0D0',
                        }}>
                        <span style={{ fontSize: '1.2rem' }}>{f.activeAvatar || f.name.charAt(0)}</span>
                        <span style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600 }}>{f.name}</span>
                        <span style={{ fontSize: '0.6rem', color: '#E57373', fontFamily: 'var(--font-display)', letterSpacing: '0.05em' }}>FIGHT</span>
                      </button>
                    ))}
                    <button className="btn-arena" onClick={() => setShowFriends(false)} style={{ alignSelf: 'center', padding: '6px 16px', fontSize: '0.6rem' }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Not eligible message */}
            {eligible && !eligible.eligible && (
              <div className="arena-surface duel-pub__section" style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '0.75rem', color: '#E57373' }}>{eligible.reason}</p>
              </div>
            )}

            {/* Daily free card — only when 0 cards */}
            {stats?.starterPackClaimed && collection.length === 0 && (
              <div className="arena-surface duel-pub__section" style={{ textAlign: 'center', padding: '16px' }}>
                <p style={{ fontSize: '0.75rem', color: '#E8E0D0', marginBottom: 10 }}>
                  No cards left! Claim a free one.
                </p>
                <button className="btn-arena btn-arena--primary" onClick={async () => {
                  const res = await fetch(`${API_BASE}/api/duel/daily-free-card`, { method: 'POST', headers: authHeaders() });
                  if (res.ok) {
                    const data = await res.json();
                    if (data.claimed) {
                      setNewCards([data.card]);
                      await loadData();
                    }
                  }
                }}>
                  Free Daily Card
                </button>
              </div>
            )}

            {/* Collection */}
            {collection.length > 0 && (
              <div className="duel-pub__section">
                <p className="drawer-title" style={{ marginBottom: 10 }}>Collection ({collection.length})</p>
                {actions.length > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <p className="drawer-section-label" style={{ color: '#8A7A60' }}>Actions</p>
                    <div className="duel-pub__card-grid">
                      {actions.map(c => <DuelCard key={c.id} cardType={c.cardType} />)}
                    </div>
                  </div>
                )}
                {perks.length > 0 && (
                  <div>
                    <p className="drawer-section-label" style={{ color: '#8A7A60' }}>Perks</p>
                    <div className="duel-pub__card-grid">
                      {perks.map(c => <DuelCard key={c.id} cardType={c.cardType} />)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Match History */}
            {history.length > 0 && (
              <div className="arena-surface duel-pub__section">
                <p className="drawer-title" style={{ marginBottom: 10 }}>Recent Duels</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {history.map(m => (
                    <div key={m.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: '6px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(218,165,32,0.08)',
                    }}>
                      <span style={{ fontSize: '1.1rem' }}>{m.opponentAvatar || m.opponentName.charAt(0)}</span>
                      <span style={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, color: '#E8E0D0' }}>{m.opponentName}</span>
                      <span style={{
                        fontSize: '0.6rem', fontFamily: 'var(--font-display)',
                        color: m.won === null ? '#6A6050' : m.won ? '#DAA520' : '#E57373',
                      }}>
                        {m.won === null ? 'CANCELLED' : m.won ? 'WON' : 'LOST'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
